import { create } from "zustand";

/**
 * The account being worked in: the user's own, or one they were invited into.
 *
 * It goes to the backend as the x-account-id header and grants nothing by
 * itself — membership is checked on every request. Kept in localStorage so the
 * choice survives a reload.
 *
 * null means no choice was made in this browser, and the account is then
 * picked for the person (see defaultAccount). Choosing the own account stores
 * its id like any other: the backend treats the own id in the header as no
 * header, and the choice has to stick for someone whose own account is empty.
 */

const STORAGE_KEY = "promobile.account.active";

const readStored = (): number | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  } catch {
    return null;
  }
};

const writeStored = (id: number | null): void => {
  try {
    if (id) window.localStorage.setItem(STORAGE_KEY, String(id));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // private mode / quota: the choice just will not survive a reload
  }
};

interface AccountState {
  activeId: number | null;
  select: (id: number | null) => void;
}

export const useAccountStore = create<AccountState>((set) => ({
  activeId: readStored(),
  select: (id) =>
    set(() => {
      writeStored(id);
      return { activeId: id };
    }),
}));

export const activeAccountId = (): number | null =>
  useAccountStore.getState().activeId;

/**
 * Switching accounts: the Apollo cache and the pages' own state are built for
 * one account, so the choice is stored and the app reloaded — cheaper and
 * safer than resetting every store by hand.
 */
export const setActiveAccountId = (id: number | null): void => {
  useAccountStore.getState().select(id);
  window.location.assign("/my-apps");
};

/**
 * Opens the account pickDefaultAccount chose: stored as the browser's choice,
 * so it is not looked up again, and the current page reloaded inside it.
 */
export const openDefaultAccount = (id: number): void => {
  useAccountStore.getState().select(id);
  window.location.reload();
};

/**
 * Logout's wipe of the browser's local data — drafts, filters, the theme — that
 * leaves the chosen account in place. A teammate's own account is usually
 * empty, so without it every login lands them where there is nothing to open
 * until they find the switcher. Someone else signing in on this browser is no
 * leak: the id grants nothing, and a user who is not a member of it gets
 * ACCOUNT_ACCESS and falls back to their own account (dropActiveAccount).
 */
export const clearLocalDataKeepingAccount = (): void => {
  const account = readStored();
  try {
    window.localStorage.clear();
    // The tab's own data goes too: unfinished new-order drafts, an import
    // queue — the next person in this tab must not find them.
    window.sessionStorage.clear();
  } catch {
    // private mode: nothing was stored, nothing to clear
  }
  writeStored(account);
};

/**
 * Access to the chosen account is gone — taken away while the person was
 * inside it. The stored id is dropped and the app reloaded into the user's own
 * account; leaving it in place would fail every request, including the one the
 * account switcher itself is built from.
 */
export const dropActiveAccount = (): void => {
  if (!activeAccountId()) return;
  useAccountStore.getState().select(null);
  window.location.assign("/my-apps");
};

/**
 * The account is still open to the person, one application in it is not: it
 * was taken out of their App Access, or the link they followed is for an app
 * they were never given. The page that asked for it cannot be shown, so they
 * are taken to My Apps — through the router, not a reload, so the message
 * explaining why stays on screen.
 */
export const leaveForbiddenApp = (): void => {
  if (window.location.pathname.startsWith("/my-apps")) return;
  window.history.pushState({}, "", "/my-apps");
  window.dispatchEvent(new PopStateEvent("popstate"));
};
